import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import template from './form-views-list.html';
import {
  createFormView,
  deleteFormView,
  getDocument,
  getFormViewData,
  listFormViews,
  updateFormView,
} from '../services/api.service';
import type {
  Document,
  FormDefinition,
  FormViewData,
  FormViewDefinition,
  FormViewSeries,
} from '../services/api.types';
import { ConfirmDialog } from '../components/confirm-dialog';

type ExportedFormView = {
  title: string;
  viewType: string;
  definitionJson: string;
};

interface FormViewExport {
  format: 'radishdms-form-views';
  version: 1;
  exportedAt: string;
  views: ExportedFormView[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Filler,
  Legend,
  Tooltip,
  zoomPlugin,
);

@customElement({ name: 'form-views-list', template })
export class FormViewsList {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public forms: FormDefinition[] = [];
  @bindable public documentOpened: ((document: Document) => void) | null = null;
  @bindable public documentOpen = false;

  public views: FormViewDefinition[] = [];
  public selectedView: FormViewDefinition | null = null;
  public selectedData: FormViewData | null = null;
  public showModal = false;
  public viewToEdit: FormViewDefinition | null = null;
  public isLoading = true;
  public isLoadingData = false;
  public errorMessage = '';
  public dataError = '';
  public importing = false;
  public showChartModal = false;
  public chartCanvas!: HTMLCanvasElement;
  public modalChartCanvas!: HTMLCanvasElement;
  public importInput: HTMLInputElement | null = null;

  private _previewChart: Chart | null = null;
  private _modalChart: Chart | null = null;
  private _dataLoadVersion = 0;
  private _keyHandler: ((event: KeyboardEvent) => void) | null = null;

  public async attached(): Promise<void> {
    this._keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.showChartModal && !this.documentOpen) {
        this.closeChartModal();
      }
    };
    document.addEventListener('keydown', this._keyHandler);
    await this.fetchViews();
  }

  public detaching(): void {
    if (this._keyHandler)
      document.removeEventListener('keydown', this._keyHandler);
    this.destroyChart();
  }

  public get hasChartData(): boolean {
    return this.selectedData?.series.some(series => series.points.length > 0) ?? false;
  }

  public async fetchViews(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      this.views = await listFormViews();
      if (this.selectedView) {
        this.selectedView = this.views.find(view => view.id === this.selectedView?.id) ?? null;
      }
      if (!this.selectedView && this.views.length > 0)
        this.selectedView = this.views[0];
      if (this.selectedView) await this.loadData(this.selectedView);
    } catch (error: unknown) {
      this.errorMessage = error instanceof Error
        ? error.message
        : this.i18n.tr('forms.views.loadFailed');
    } finally {
      this.isLoading = false;
    }
  }

  public openCreateModal(): void {
    this.viewToEdit = null;
    this.showModal = true;
  }

  public openEditModal(view: FormViewDefinition): void {
    this.viewToEdit = view;
    this.showModal = true;
  }

  public exportView(view: FormViewDefinition): void {
    const exportData: FormViewExport = {
      format: 'radishdms-form-views',
      version: 1,
      exportedAt: new Date().toISOString(),
      views: [{
        title: view.title,
        viewType: view.viewType,
        definitionJson: view.definitionJson,
      }],
    };

    this.downloadExport(
      exportData,
      `radishdms-form-view-${this.safeFilePart(view.title)}.json`,
    );
  }

  public openImportPicker(): void {
    this.importInput?.click();
  }

  public async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.importing) return;

    this.errorMessage = '';
    this.importing = true;

    try {
      const importedViews = this.parseViewExport(await file.text());
      const existingViews = new Map(
        this.views.map(view => [this.normalizeTitle(view.title), view]),
      );
      const failures: string[] = [];

      for (const importedView of importedViews) {
        const titleKey = this.normalizeTitle(importedView.title);
        const existingView = existingViews.get(titleKey);
        if (existingView) {
          const confirmed = await ConfirmDialog.confirm(
            this.i18n.tr('forms.views.import.overwrite', { title: importedView.title }),
            this.i18n.tr('forms.views.import.overwriteTitle'),
          );
          if (!confirmed) continue;
        }

        const dto = {
          title: importedView.title,
          viewType: importedView.viewType,
          definitionJson: importedView.definitionJson,
        };

        try {
          const saved = existingView
            ? await updateFormView(existingView.id, dto)
            : await createFormView(dto);
          existingViews.set(titleKey, saved);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : String(error);
          failures.push(`${importedView.title}: ${message}`);
        }
      }

      await this.fetchViews();
      if (failures.length > 0) {
        await ConfirmDialog.alert(
          `${this.i18n.tr('forms.views.import.failed', { count: failures.length })} ${failures.join(' ')}`,
          this.i18n.tr('common.error'),
        );
      }
    } catch (error: unknown) {
      await ConfirmDialog.alert(
        error instanceof Error
          ? error.message
          : this.i18n.tr('forms.views.import.invalidFile'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.importing = false;
    }
  }

  public editViewFromRow(event: Event, view: FormViewDefinition): void {
    event.stopPropagation();
    this.openEditModal(view);
  }

  public removeViewFromRow(event: Event, view: FormViewDefinition): void {
    event.stopPropagation();
    void this.removeView(view);
  }

  public async selectView(view: FormViewDefinition): Promise<void> {
    if (this.selectedView?.id === view.id && this.selectedData) return;
    this.selectedView = view;
    await this.loadData(view);
  }

  public async removeView(view: FormViewDefinition): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('forms.views.delete.confirm', { title: view.title }),
      this.i18n.tr('forms.views.delete.confirmTitle'),
    );
    if (!confirmed) return;

    try {
      await deleteFormView(view.id);
      this.views = this.views.filter(existing => existing.id !== view.id);
      if (this.selectedView?.id === view.id) {
        this.destroyChart();
        this.selectedData = null;
        this.selectedView = this.views[0] ?? null;
        if (this.selectedView) await this.loadData(this.selectedView);
      }
    } catch (error: unknown) {
      this.errorMessage = error instanceof Error ? error.message : String(error);
    }
  }

  public onModalSaved(view: FormViewDefinition): void {
    const index = this.views.findIndex(existing => existing.id === view.id);
    this.views = index >= 0
      ? this.views.map(existing => existing.id === view.id ? view : existing)
      : [...this.views, view];
    this.selectedView = view;
    this.closeModal();
    void this.loadData(view);
  }

  public onModalClosed(): void {
    this.closeModal();
  }

  public openChartModal(): void {
    if (!this.selectedView || !this.hasChartData) return;
    this.showChartModal = true;
    requestAnimationFrame(() => {
      if (this.showChartModal)
        this.renderChart(this.modalChartCanvas, true);
    });
  }

  public closeChartModal(): void {
    this.showChartModal = false;
    this._modalChart?.destroy();
    this._modalChart = null;
    requestAnimationFrame(() => {
      if (!this.showChartModal)
        this.renderChart(this.chartCanvas, false);
    });
  }

  public zoomIn(): void {
    this._modalChart?.zoom(1.25);
  }

  public zoomOut(): void {
    this._modalChart?.zoom(0.8);
  }

  public resetZoom(): void {
    this._modalChart?.resetZoom();
  }

  public async openPointDocument(
    datasetIndex: number,
    pointIndex: number,
  ): Promise<void> {
    const series = this.visibleSeries()[datasetIndex];
    const documentId = series?.points[pointIndex]?.documentId;
    if (!documentId) return;

    try {
      const document = await getDocument(documentId);
      this.documentOpened?.(document);
    } catch (error: unknown) {
      await ConfirmDialog.alert(
        error instanceof Error
          ? error.message
          : this.i18n.tr('forms.views.documentLoadFailed'),
        this.i18n.tr('common.error'),
      );
    }
  }

  public viewSummary(view: FormViewDefinition): string {
    try {
      const definition = JSON.parse(view.definitionJson) as {
        formTitle?: string;
        seriesField?: string;
        valueField?: string;
        bucket?: string;
        metrics?: Array<{ label?: string }>;
      };
      if (definition.metrics) {
        return this.i18n.tr('forms.views.summaryCalculated', {
          form: definition.formTitle ?? '',
          count: definition.metrics.length,
          bucket: this.i18n.tr(`forms.views.form.bucket${this.capitalize(definition.bucket ?? 'record')}`),
        });
      }
      return this.i18n.tr('forms.views.summary', {
        form: definition.formTitle ?? '',
        seriesField: definition.seriesField ?? '',
        valueField: definition.valueField ?? '',
      });
    } catch {
      return view.viewType;
    }
  }

  public viewChartType(view: FormViewDefinition): string {
    try {
      const definition = JSON.parse(view.definitionJson) as {
        chart?: { type?: string };
      };
      return definition.chart?.type === 'bar'
        ? this.i18n.tr('forms.views.bar')
        : this.i18n.tr('forms.views.line');
    } catch {
      return this.i18n.tr('forms.views.line');
    }
  }

  private parseViewExport(text: string): ExportedFormView[] {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      throw new Error(this.i18n.tr('forms.views.import.invalidFile'));
    }

    const entries = Array.isArray(parsed)
      ? parsed
      : isRecord(parsed) && Array.isArray(parsed.views)
        ? parsed.views
        : null;
    if (!entries) throw new Error(this.i18n.tr('forms.views.import.invalidFile'));
    if (entries.length === 0) throw new Error(this.i18n.tr('forms.views.import.empty'));

    return entries.map((entry, index) => {
      if (!isRecord(entry) ||
          typeof entry.title !== 'string' ||
          !entry.title.trim() ||
          typeof entry.viewType !== 'string' ||
          !entry.viewType.trim() ||
          typeof entry.definitionJson !== 'string' ||
          !entry.definitionJson.trim()) {
        throw new Error(this.i18n.tr('forms.views.import.invalidView', {
          index: index + 1,
        }));
      }

      try {
        JSON.parse(entry.definitionJson);
      } catch {
        throw new Error(this.i18n.tr('forms.views.import.invalidView', {
          index: index + 1,
        }));
      }

      return {
        title: entry.title.trim(),
        viewType: entry.viewType.trim(),
        definitionJson: entry.definitionJson,
      };
    });
  }

  private downloadExport(exportData: FormViewExport, fileName: string): void {
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  private safeFilePart(value: string): string {
    return value
      .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
      .trim()
      .slice(0, 80) || 'view';
  }

  private normalizeTitle(title: string): string {
    return title.trim().toLocaleLowerCase();
  }

  private async loadData(view: FormViewDefinition): Promise<void> {
    const loadVersion = ++this._dataLoadVersion;
    this.isLoadingData = true;
    this.dataError = '';
    this.selectedData = null;
    this.destroyChart();
    try {
      const data = await getFormViewData(view.id);
      if (loadVersion !== this._dataLoadVersion || this.selectedView?.id !== view.id)
        return;
      this.selectedData = data;
      requestAnimationFrame(() => this.renderChart(this.chartCanvas, false));
    } catch (error: unknown) {
      if (loadVersion !== this._dataLoadVersion || this.selectedView?.id !== view.id)
        return;
      this.dataError = error instanceof Error
        ? error.message
        : this.i18n.tr('forms.views.dataLoadFailed');
    } finally {
      if (loadVersion === this._dataLoadVersion)
        this.isLoadingData = false;
    }
  }

  private renderChart(canvas: HTMLCanvasElement | undefined, isModal: boolean): void {
    if (!canvas || !this.hasChartData) return;

    if (isModal) {
      this._modalChart?.destroy();
      this._modalChart = null;
    } else {
      this._previewChart?.destroy();
      this._previewChart = null;
    }

    const colors = ['#6366f1', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    const visibleSeries = this.visibleSeries();
    const metricIds = Array.from(new Set(visibleSeries.map(series => series.metricId)));
    const pointMarkers = this.selectedData?.pointMarkers ?? 'all';
    const chart = new Chart(canvas, {
      type: this.selectedData?.chartType ?? 'line',
      data: {
        datasets: visibleSeries.map((series, index) => ({
            label: series.name,
            data: series.points.map(point => ({
              x: Date.parse(point.documentDate),
              y: point.value,
            })),
            borderColor: series.color || colors[index % colors.length],
            backgroundColor: series.color || colors[index % colors.length],
            borderDash: (this.selectedData?.chartType ?? 'line') === 'line'
              ? this.metricDash(metricIds.indexOf(series.metricId))
              : undefined,
            pointRadius: pointMarkers === 'none'
              ? 0
              : pointMarkers === 'formDates'
                ? series.points.map(point => point.isFormDate ? 4 : 0)
                : 4,
            pointHoverRadius: pointMarkers === 'none'
              ? 0
              : pointMarkers === 'formDates'
                ? series.points.map(point => point.isFormDate ? 6 : 0)
                : 6,
            tension: 0.25,
            fill: false,
          })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: false },
        onClick: (_event, elements) => {
          const element = elements[0];
          if (element)
            void this.openPointDocument(element.datasetIndex, element.index);
        },
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              title: items => items.length > 0
                ? this.formatDate(Number(items[0].parsed.x))
                : '',
              label: item => {
                const series = visibleSeries[item.datasetIndex];
                const unit = series?.unit ? ` ${series.unit}` : '';
                return `${item.dataset.label ?? ''}: ${item.formattedValue}${unit}`;
              },
            },
          },
          zoom: {
            pan: {
              enabled: isModal,
              mode: 'xy',
            },
            zoom: {
              wheel: { enabled: isModal },
              pinch: { enabled: isModal },
              mode: 'xy',
            },
          },
        },
        scales: {
          x: {
            type: 'linear',
            title: { display: true, text: this.i18n.tr('forms.views.chart.date') },
            ticks: {
              callback: value => this.formatDate(Number(value)),
              maxTicksLimit: 8,
            },
          },
          y: {
            title: {
              display: true,
              text: this.selectedData?.yAxisLabel
                || this.i18n.tr('forms.views.chart.value'),
            },
            beginAtZero: false,
          },
        },
      },
    });

    if (isModal) this._modalChart = chart;
    else this._previewChart = chart;
  }

  private formatDate(timestamp: number): string {
    if (!Number.isFinite(timestamp)) return '';
    return new Date(timestamp).toLocaleDateString();
  }

  private visibleSeries(): FormViewSeries[] {
    return (this.selectedData?.series ?? [])
      .filter(series => series.points.length > 0);
  }

  private metricDash(index: number): number[] | undefined {
    return [
      undefined,
      [8, 4],
      [2, 3],
      [10, 3, 2, 3],
    ][index % 4];
  }

  private capitalize(value: string): string {
    return value.length > 0
      ? `${value[0].toUpperCase()}${value.slice(1)}`
      : value;
  }

  private destroyChart(): void {
    this._previewChart?.destroy();
    this._previewChart = null;
    this._modalChart?.destroy();
    this._modalChart = null;
  }

  private closeModal(): void {
    this.showModal = false;
    this.viewToEdit = null;
  }
}
